import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calculator,
  CheckCircle2,
  AlertCircle,
  Send,
  Calendar,
  Package,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

export default function VendorPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bidInfo, setBidInfo] = useState(null);
  const [prices, setPrices] = useState({});
  const [deliveryDays, setDeliveryDays] = useState(14);
  const [notes, setNotes] = useState("");
  const [excludedIds, setExcludedIds] = useState(new Set());

  const loadBid = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/bid/${token}`);
      setBidInfo(res.data);
      // initialize price state
      const init = {};
      (res.data.items || []).forEach((it) => {
        init[it.id] = "";
      });
      setPrices(init);
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.detail ||
          "Invalid or expired bid link. Please contact the project manager."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadBid();
  }, [loadBid]);

  const handlePriceChange = (id, value) => {
    setPrices((prev) => ({ ...prev, [id]: value }));
  };

  const grandTotal = (bidInfo?.items || []).reduce((sum, it) => {
    if (excludedIds.has(it.id)) return sum;
    const unit = parseFloat(prices[it.id]) || 0;
    return sum + unit * (it.quantity || 1);
  }, 0);

  const toggleExclude = (id) => {
    const next = new Set(excludedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcludedIds(next);
  };

  const handleSubmit = async () => {
    if (!bidInfo) return;

    // Only validate prices for non-excluded items
    const required = bidInfo.items.filter((it) => !excludedIds.has(it.id));
    const missing = required.filter(
      (it) => !prices[it.id] || isNaN(parseFloat(prices[it.id]))
    );
    if (missing.length > 0) {
      toast.error(`Please provide unit price for all ${required.length} included items`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: bidInfo.items.map((it) => ({
          id: it.id,
          description: it.description,
          quantity: it.quantity,
          unit: it.unit,
          unit_price: excludedIds.has(it.id) ? 0 : parseFloat(prices[it.id]) || 0,
        })),
        delivery_days: parseInt(deliveryDays) || 0,
        notes: notes || "",
        excluded_item_ids: Array.from(excludedIds),
      };
      const res = await axios.post(`${API}/public/bid/${token}`, payload);
      toast.success(`Quote submitted! Total: ${formatCurrency(res.data.total)}`);
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="vendor-loading">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="vendor-error">
        <div className="max-w-md text-center swiss-card">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-heading font-bold mb-2">Unable to load bid</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="vendor-success">
        <div className="max-w-md text-center swiss-card">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-xl font-heading font-bold mb-2">Quote submitted!</h1>
          <p className="text-muted-foreground mb-4">
            Thank you, <strong>{bidInfo.vendor_name}</strong>. Your quote was received and the
            project manager will review it shortly.
          </p>
          <p className="text-sm text-muted-foreground">
            Total submitted: <span className="font-mono font-semibold">{formatCurrency(grandTotal)}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="vendor-portal">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4 flex items-center gap-3">
          <Calculator className="w-7 h-7 text-primary" />
          <div className="flex-1">
            <span className="font-heading font-bold text-xl">
              Estimate<span className="text-primary">Engine</span>
            </span>
            <p className="text-xs text-muted-foreground">Vendor Bid Portal</p>
          </div>
          <Badge className="bg-blue-100 text-blue-700">Bid Package</Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10 space-y-6">
        {/* Project Info */}
        <section className="swiss-card">
          <h1 className="text-2xl font-heading font-bold mb-1" data-testid="bid-project-name">
            {bidInfo.project_name}
          </h1>
          <p className="text-muted-foreground mb-4">{bidInfo.package_name}</p>

          {bidInfo.package_description && (
            <p className="text-sm text-foreground mb-4">{bidInfo.package_description}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Vendor:</span>
              <span className="font-medium">{bidInfo.vendor_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Items:</span>
              <span className="font-medium">{bidInfo.items?.length || 0}</span>
            </div>
            {bidInfo.deadline && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Deadline:</span>
                <span className="font-medium">{bidInfo.deadline}</span>
              </div>
            )}
          </div>
        </section>

        {/* Documents (visible to vendor) */}
        {bidInfo.visible_docs?.length > 0 && (
          <section className="swiss-card">
            <h2 className="text-lg font-heading font-semibold mb-3">Reference Documents</h2>
            <p className="text-xs text-muted-foreground mb-3">For reference only — names visible to you.</p>
            <ul className="space-y-1">
              {bidInfo.visible_docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-sm border rounded p-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>{d.name}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Items Table */}
        <section className="swiss-card">
          <h2 className="text-lg font-heading font-semibold mb-4">Scope Items</h2>
          <p className="text-xs text-muted-foreground mb-3">Tick "Exclude" if you don't want to quote a specific item.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-2 font-medium text-muted-foreground">#</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground">Exclude</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground">Room</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground">Description</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground text-right">Qty</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground">Unit</th>
                  <th className="py-2 pr-2 font-medium text-muted-foreground text-right">Unit Price</th>
                  <th className="py-2 pl-2 font-medium text-muted-foreground text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(bidInfo.items || []).map((it, idx) => {
                  const excluded = excludedIds.has(it.id);
                  const unit = parseFloat(prices[it.id]) || 0;
                  const total = excluded ? 0 : unit * (it.quantity || 1);
                  return (
                    <tr key={it.id} className={`border-b border-border/50 ${excluded ? "opacity-40" : ""}`} data-testid={`bid-item-${idx}`}>
                      <td className="py-3 pr-2 text-muted-foreground">{idx + 1}</td>
                      <td className="py-3 pr-2">
                        <input type="checkbox" checked={excluded} onChange={() => toggleExclude(it.id)} data-testid={`exclude-item-${idx}`} />
                      </td>
                      <td className="py-3 pr-2">{it.room || "—"}</td>
                      <td className="py-3 pr-2">{it.description}</td>
                      <td className="py-3 pr-2 text-right font-mono">{it.quantity}</td>
                      <td className="py-3 pr-2">{it.unit}</td>
                      <td className="py-3 pr-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={prices[it.id] ?? ""}
                          onChange={(e) => handlePriceChange(it.id, e.target.value)}
                          placeholder="0.00"
                          className="w-28 text-right font-mono"
                          disabled={excluded}
                          data-testid={`bid-price-input-${idx}`}
                        />
                      </td>
                      <td className="py-3 pl-2 text-right font-mono">{formatCurrency(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7} className="py-3 pr-2 text-right font-semibold">
                    Grand Total:
                  </td>
                  <td className="py-3 pl-2 text-right font-mono font-bold text-lg" data-testid="bid-grand-total">
                    {formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* Delivery + Notes */}
        <section className="swiss-card">
          <h2 className="text-lg font-heading font-semibold mb-4">Delivery & Notes</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="delivery_days">Delivery Time (days)</Label>
              <Input
                id="delivery_days"
                type="number"
                min="1"
                value={deliveryDays}
                onChange={(e) => setDeliveryDays(e.target.value)}
                data-testid="bid-delivery-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes / Terms / Warranty</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, warranty, exclusions, lead times..."
              rows={4}
              data-testid="bid-notes-textarea"
            />
          </div>
        </section>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6">
          <p className="text-sm text-muted-foreground">
            By submitting, you confirm the prices and terms above are valid.
          </p>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={submitting}
            className="gap-2"
            data-testid="submit-bid-btn"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Submit Quote
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
